import UserService from "../UserService";
import {
  FhirBundle,
  FhirClientPort,
  FhirResource,
  FhirSearchValue,
} from "./fhir.types";

export class FhirHttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly responseBody?: unknown,
  ) {
    super(message);
    this.name = "FhirHttpError";
  }
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.trim().replace(/\/$/, "");
}

function appendSearchValue(
  params: URLSearchParams,
  key: string,
  value: FhirSearchValue,
): void {
  if (value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => params.append(key, String(item)));
    return;
  }

  params.append(key, String(value));
}

function extractOperationOutcomeMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") {
    return undefined;
  }

  const outcome = body as {
    resourceType?: string;
    issue?: Array<{ diagnostics?: string; details?: { text?: string } }>;
  };

  if (outcome.resourceType !== "OperationOutcome") {
    return undefined;
  }

  return outcome.issue
    ?.map((issue) => issue.diagnostics || issue.details?.text)
    .find(Boolean);
}

export class FhirHttpClient implements FhirClientPort {
  private readonly baseUrl: string;

  constructor(
    baseUrl = process.env.REACT_APP_FHIR_URL ?? "",
    private readonly tokenProvider: () =>
      | string
      | undefined
      | Promise<string | undefined> = UserService.getValidToken,
  ) {
    this.baseUrl = normalizeBaseUrl(baseUrl);
  }

  async search(
    resourceType: string,
    searchParams: Readonly<Record<string, FhirSearchValue>> = {},
  ): Promise<FhirBundle> {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) =>
      appendSearchValue(params, key, value),
    );

    const query = params.toString();
    const baseUrl = this.configuredBaseUrl();
    const url = `${baseUrl}/${resourceType}${query ? `?${query}` : ""}`;
    const resource = await this.request(url);

    if (resource.resourceType !== "Bundle") {
      throw new FhirHttpError(
        200,
        `Expected a FHIR Bundle from ${resourceType} search.`,
        resource,
      );
    }

    return resource as FhirBundle;
  }

  async read(resourceType: string, id: string): Promise<FhirResource> {
    return this.request(
      `${this.configuredBaseUrl()}/${resourceType}/${encodeURIComponent(id)}`,
    );
  }

  async create(
    resourceType: string,
    resource: FhirResource,
  ): Promise<FhirResource> {
    return this.request(`${this.configuredBaseUrl()}/${resourceType}`, {
      method: "POST",
      body: JSON.stringify(resource),
    });
  }

  async transaction(bundle: FhirBundle): Promise<FhirBundle> {
    const resource = await this.request(this.configuredBaseUrl(), {
      method: "POST",
      body: JSON.stringify(bundle),
    });

    if (resource.resourceType !== "Bundle") {
      throw new FhirHttpError(
        200,
        "Expected a FHIR Bundle transaction response.",
        resource,
      );
    }

    return resource as FhirBundle;
  }

  private configuredBaseUrl(): string {
    if (!this.baseUrl) {
      throw new Error("REACT_APP_FHIR_URL is not configured.");
    }

    return this.baseUrl;
  }

  private async request(
    url: string,
    init: RequestInit = {},
  ): Promise<FhirResource> {
    const token = await this.tokenProvider();
    const response = await fetch(url, {
      ...init,
      headers: {
        Accept: "application/fhir+json",
        ...(init.body ? { "Content-Type": "application/fhir+json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }

    if (!response.ok) {
      const outcomeMessage = extractOperationOutcomeMessage(body);
      throw new FhirHttpError(
        response.status,
        outcomeMessage || `FHIR request failed with status ${response.status}.`,
        body,
      );
    }

    if (!body || typeof body !== "object" || !("resourceType" in body)) {
      throw new FhirHttpError(
        response.status,
        "FHIR response does not contain a resourceType.",
        body,
      );
    }

    return body as FhirResource;
  }
}
